import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import { useNavigate, useParams } from 'react-router-dom';
import {
  createEmailCampaign,
  deleteEmailCampaignAttachment,
  estimateEmailCampaignRecipients,
  getEmailCampaign,
  getEmailCampaignLookups,
  mapLaravelError,
  scheduleEmailCampaign,
  searchEmailCampaignEmployees,
  sendEmailCampaignTest,
  updateEmailCampaign,
  uploadEmailCampaignAttachments,
} from '../../services/emailCampaignsApi';
import EmailCampaignAudienceStep from './components/EmailCampaignAudienceStep';
import EmailCampaignContentStep from './components/EmailCampaignContentStep';
import EmailCampaignScheduleStep from './components/EmailCampaignScheduleStep';
import EmailCampaignAttachmentsStep from './components/EmailCampaignAttachmentsStep';
import EmailCampaignReviewStep from './components/EmailCampaignReviewStep';
import EmailCampaignPreviewDialog from './components/EmailCampaignPreviewDialog';
import EmailCampaignTourDialog from './components/EmailCampaignTourDialog';

const STEPS = ['Audience', 'Content', 'Schedule', 'Attachments', 'Review'];
const BUILDER_TOUR_STORAGE_KEY = 'email_campaigns_tour_builder_v1';
const BUILDER_TOUR_STEPS = [
  {
    title: 'Step-by-step Builder',
    description: 'Move through Audience, Content, Schedule, Attachments, and Review using Save & Next.',
  },
  {
    title: 'Visual Email Designer',
    description: 'In Content, use Visual Builder for drag-and-drop blocks, quick templates, and variable insertion.',
  },
  {
    title: 'Preview and Test',
    description: 'Use Preview and Send Test before scheduling to validate content and personalization.',
  },
  {
    title: 'Schedule with Confidence',
    description: 'Review recipient estimate and confirm the final schedule in the last step.',
  },
];

const toInputDateTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const pad = (n) => String(n).padStart(2, '0');
  const yyyy = date.getFullYear();
  const mm = pad(date.getMonth() + 1);
  const dd = pad(date.getDate());
  const hh = pad(date.getHours());
  const ii = pad(date.getMinutes());

  return `${yyyy}-${mm}-${dd}T${hh}:${ii}`;
};

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());

const normalizeCampaign = (item = {}) => ({
  id: item.id,
  title: item.title || 'Untitled campaign',
  status: item.status || 'draft',
  audience_mode: item.audience_mode || 'all',
  audience_rules: item.audience_rules || { active_only: true },
  subject: item.subject || '',
  preheader: item.preheader || '',
  html_body: item.html_body || '',
  html_body_en: item.html_body_en || '',
  html_body_ar: item.html_body_ar || '',
  text_body: item.text_body || '',
  text_body_en: item.text_body_en || '',
  text_body_ar: item.text_body_ar || '',
  is_bilingual: Boolean(item.is_bilingual),
  from_name: item.from_name || '',
  from_email: item.from_email || '',
  reply_to: item.reply_to || '',
  send_in_app_notification: Boolean(item.send_in_app_notification),
  timezone: item.timezone || 'Asia/Riyadh',
  send_mode: item.send_mode || 'once',
  send_at: toInputDateTime(item.send_at),
  recurrence: item.recurrence || { frequency: 'daily', interval: 1, end: { type: 'never' } },
  attachments: Array.isArray(item.attachments) ? item.attachments : [],
});

const toPayload = (campaign) => ({
  title: campaign.title,
  audience_mode: campaign.audience_mode,
  audience_rules: campaign.audience_rules || {},
  subject: campaign.subject,
  preheader: campaign.preheader,
  html_body: campaign.html_body,
  html_body_en: campaign.html_body_en,
  html_body_ar: campaign.html_body_ar,
  text_body: campaign.text_body,
  text_body_en: campaign.text_body_en,
  text_body_ar: campaign.text_body_ar,
  is_bilingual: Boolean(campaign.is_bilingual),
  from_name: campaign.from_name,
  from_email: campaign.from_email,
  reply_to: campaign.reply_to,
  send_in_app_notification: Boolean(campaign.send_in_app_notification),
  timezone: campaign.timezone || 'Asia/Riyadh',
});

const mergeServerCampaignWithLocalSchedule = (serverCampaign, localCampaign) => ({
  ...serverCampaign,
  send_mode: localCampaign?.send_mode ?? serverCampaign.send_mode,
  send_at: localCampaign?.send_at ?? serverCampaign.send_at,
  recurrence: localCampaign?.recurrence ?? serverCampaign.recurrence,
  timezone: localCampaign?.timezone || serverCampaign.timezone,
});

const applySenderDefaults = (value = {}, defaults = {}) => ({
  ...value,
  from_name: String(value?.from_name || defaults?.from_name || '').trim(),
  from_email: String(value?.from_email || defaults?.from_email || '').trim(),
  timezone: value?.timezone || defaults?.timezone || 'Asia/Riyadh',
});

export default function EmailCampaignBuilderPage() {
  const navigate = useNavigate();
  const params = useParams();
  const routeId = params.id ? Number(params.id) : null;

  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendTestLoading, setSendTestLoading] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [scheduleFieldErrors, setScheduleFieldErrors] = useState({});

  const [lookups, setLookups] = useState({});
  const [campaign, setCampaign] = useState(normalizeCampaign());
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [tourOpen, setTourOpen] = useState(false);

  const [estimatedRecipients, setEstimatedRecipients] = useState(0);
  const [estimateLoading, setEstimateLoading] = useState(false);

  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [employeeLoading, setEmployeeLoading] = useState(false);
  const [selectedEmployees, setSelectedEmployees] = useState([]);

  const isReadOnly = ['sending', 'sent'].includes(String(campaign?.status || '').toLowerCase());

  const refreshCampaign = useCallback(async (campaignId, options = {}) => {
    const preserveLocalSchedule = Boolean(options?.preserveLocalSchedule);
    const payload = await getEmailCampaign(campaignId);
    const normalized = applySenderDefaults(
      normalizeCampaign(payload?.data || {}),
      options?.defaults || {}
    );
    setCampaign((prev) => (
      preserveLocalSchedule
        ? mergeServerCampaignWithLocalSchedule(normalized, prev)
        : normalized
    ));
    setEstimatedRecipients(Number(payload?.data?.recipient_snapshot_count || 0));
    if (Array.isArray(normalized?.audience_rules?.employee_ids)) {
      setSelectedEmployees(
        normalized.audience_rules.employee_ids.map((id) => ({
          id,
          name: `Employee #${id}`,
          email: '',
        }))
      );
    }
  }, []);

  const init = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const lookupData = await getEmailCampaignLookups();
      setLookups(lookupData || {});

      if (routeId) {
        await refreshCampaign(routeId, { defaults: lookupData?.defaults || {} });
      } else {
        const created = await createEmailCampaign({
          title: `Untitled Campaign ${new Date().toISOString().slice(0, 16)}`,
          audience_mode: 'all',
          audience_rules: { active_only: true },
          timezone: lookupData?.defaults?.timezone || 'Asia/Riyadh',
        });

        setCampaign(applySenderDefaults(normalizeCampaign(created?.data || {}), lookupData?.defaults || {}));
      }
    } catch (requestError) {
      setError(mapLaravelError(requestError).message);
    } finally {
      setLoading(false);
    }
  }, [refreshCampaign, routeId]);

  useEffect(() => {
    init();
  }, [init]);

  useEffect(() => {
    try {
      const seen = window.localStorage.getItem(BUILDER_TOUR_STORAGE_KEY);
      if (!seen) {
        setTourOpen(true);
      }
    } catch {
      // ignore
    }
  }, []);

  const saveDraft = useCallback(async () => {
    if (!campaign?.id) return null;

    setSaving(true);
    setError('');

    try {
      const payload = await updateEmailCampaign(campaign.id, toPayload(campaign));
      const normalized = normalizeCampaign(payload?.data || {});
      const merged = mergeServerCampaignWithLocalSchedule(normalized, campaign);
      setCampaign(merged);
      return merged;
    } catch (requestError) {
      setError(mapLaravelError(requestError).message);
      throw requestError;
    } finally {
      setSaving(false);
    }
  }, [campaign]);

  useEffect(() => {
    if (!campaign?.id) return undefined;

    const timer = window.setTimeout(async () => {
      setEstimateLoading(true);

      try {
        const payload = await estimateEmailCampaignRecipients(campaign.id, {
          audience_mode: campaign.audience_mode,
          audience_rules: campaign.audience_rules || {},
        });

        setEstimatedRecipients(Number(payload?.estimated_recipients || 0));
      } catch (requestError) {
        setEstimatedRecipients(0);
      } finally {
        setEstimateLoading(false);
      }
    }, 450);

    return () => window.clearTimeout(timer);
  }, [campaign.id, campaign.audience_mode, campaign.audience_rules]);

  const handleSearchEmployees = useCallback(async (query) => {
    if (!campaign?.id) return;

    setEmployeeLoading(true);
    try {
      const payload = await searchEmailCampaignEmployees({
        q: query,
        per_page: 30,
      });
      setEmployeeOptions(Array.isArray(payload?.data) ? payload.data : []);
    } catch {
      setEmployeeOptions([]);
    } finally {
      setEmployeeLoading(false);
    }
  }, [campaign?.id]);

  const nextStep = async () => {
    if (!isReadOnly) {
      try {
        await saveDraft();
      } catch {
        return;
      }
    }
    setStep((prev) => Math.min(STEPS.length - 1, prev + 1));
  };

  const previousStep = () => setStep((prev) => Math.max(0, prev - 1));

  const handleSendTest = async ({ email, name }) => {
    if (!campaign?.id || !String(email || '').trim()) {
      return;
    }

    setSendTestLoading(true);
    setError('');
    setSuccess('');

    try {
      if (!isReadOnly) {
        await saveDraft();
      }

      await sendEmailCampaignTest(campaign.id, { email, name });
      setSuccess('Test email queued successfully.');
    } catch (requestError) {
      setError(mapLaravelError(requestError).message);
    } finally {
      setSendTestLoading(false);
    }
  };

  const handleUploadAttachments = async (files) => {
    if (!campaign?.id || !files.length) return;

    setUploadLoading(true);
    setError('');

    try {
      await uploadEmailCampaignAttachments(campaign.id, files);
      await refreshCampaign(campaign.id, { preserveLocalSchedule: true });
      setSuccess('Attachments uploaded.');
    } catch (requestError) {
      setError(mapLaravelError(requestError).message);
    } finally {
      setUploadLoading(false);
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    if (!campaign?.id) return;

    setError('');

    try {
      await deleteEmailCampaignAttachment(campaign.id, attachmentId);
      await refreshCampaign(campaign.id, { preserveLocalSchedule: true });
      setSuccess('Attachment removed.');
    } catch (requestError) {
      setError(mapLaravelError(requestError).message);
    }
  };

  const handleConfirmSchedule = async () => {
    if (!campaign?.id) return;

    const localErrors = {};
    const resolvedFromEmail = String(campaign.from_email || lookups?.defaults?.from_email || '').trim();

    if (!isValidEmail(resolvedFromEmail)) {
      localErrors.from_email = 'A valid from email is required before scheduling.';
    }

    if (String(campaign.send_mode || '') === 'once' && !String(campaign.send_at || '').trim()) {
      localErrors.send_at = 'send_at is required for send_mode=once.';
    }

    if (Object.keys(localErrors).length > 0) {
      setScheduleFieldErrors(localErrors);
      setError(localErrors.from_email || localErrors.send_at || 'Please complete schedule fields.');
      setStep(localErrors.from_email ? 1 : 2);
      return;
    }

    setConfirmLoading(true);
    setError('');
    setSuccess('');
    setScheduleFieldErrors({});

    try {
      const schedulePayload = {
        send_mode: campaign.send_mode,
        timezone: campaign.timezone || 'Asia/Riyadh',
        send_at: String(campaign.send_at || '').trim() || undefined,
        recurrence: campaign.send_mode === 'recurring' ? campaign.recurrence : undefined,
      };

      if (!isReadOnly) {
        await saveDraft();
      }

      await scheduleEmailCampaign(campaign.id, schedulePayload);

      navigate(`/admin/communications/email-campaigns/${campaign.id}`);
    } catch (requestError) {
      const mapped = mapLaravelError(requestError);
      setError(mapped.message);
      setScheduleFieldErrors((prev) => ({
        ...prev,
        ...(mapped.fieldErrors || {}),
      }));
      if (mapped.fieldErrors?.send_at) {
        setStep(2);
      }
    } finally {
      setConfirmLoading(false);
    }
  };

  const openPreview = () => {
    const html = campaign.is_bilingual
      ? `<div dir="ltr">${campaign.html_body_en || campaign.html_body || ''}</div><hr /><div dir="rtl" style="text-align:right">${campaign.html_body_ar || ''}</div>`
      : campaign.html_body || '';

    setPreviewHtml(html);
    setPreviewOpen(true);
  };

  const finishTour = () => {
    // Once the tour is closed (any way), remember it so it never auto-pops again.
    try {
      window.localStorage.setItem(BUILDER_TOUR_STORAGE_KEY, '1');
    } catch {
      // ignore
    }
    setTourOpen(false);
  };

  let stepContent = (
    <EmailCampaignReviewStep
      campaign={campaign}
      estimatedRecipients={estimatedRecipients}
      onConfirm={handleConfirmSchedule}
      confirmLoading={confirmLoading}
      readOnly={isReadOnly}
    />
  );

  if (step === 0) {
    stepContent = (
      <EmailCampaignAudienceStep
        value={campaign}
        onChange={setCampaign}
        lookups={lookups}
        selectedEmployees={selectedEmployees}
        onSelectedEmployeesChange={setSelectedEmployees}
        employeeOptions={employeeOptions}
        onEmployeeSearch={handleSearchEmployees}
        employeeLoading={employeeLoading}
        estimatedRecipients={estimatedRecipients}
        estimateLoading={estimateLoading}
        readOnly={isReadOnly}
      />
    );
  } else if (step === 1) {
    stepContent = (
      <EmailCampaignContentStep
        value={campaign}
        onChange={setCampaign}
        placeholders={lookups?.placeholders || []}
        onSendTest={handleSendTest}
        sendTestLoading={sendTestLoading}
        onPreview={openPreview}
        readOnly={isReadOnly}
      />
    );
  } else if (step === 2) {
    stepContent = (
      <EmailCampaignScheduleStep
        value={campaign}
        onChange={setCampaign}
        readOnly={isReadOnly}
        fieldErrors={scheduleFieldErrors}
      />
    );
  } else if (step === 3) {
    stepContent = (
      <EmailCampaignAttachmentsStep
        attachments={campaign.attachments || []}
        limits={lookups?.limits?.attachments || {}}
        onUpload={handleUploadAttachments}
        onDelete={handleDeleteAttachment}
        uploadLoading={uploadLoading}
        readOnly={isReadOnly}
      />
    );
  }

  if (loading) {
    return (
      <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <Stack spacing={2}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {routeId ? 'Edit Email Campaign' : 'New Email Campaign'}
            </Typography>
            <Chip size="small" label={`Step ${step + 1} of ${STEPS.length}: ${STEPS[step]}`} color="primary" variant="outlined" />
          </Stack>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'stretch', md: 'center' }}>
            <Button
              variant="outlined"
              startIcon={<HelpOutlineOutlinedIcon />}
              onClick={() => setTourOpen(true)}
            >
              Tour
            </Button>
            <TextField
              label="Campaign title"
              size="small"
              value={campaign.title || ''}
              onChange={(event) => setCampaign((prev) => ({ ...prev, title: event.target.value }))}
              sx={{ minWidth: 320 }}
              disabled={isReadOnly}
            />
            <FormControlLabel
              control={(
                <Switch
                  checked={Boolean(campaign.send_in_app_notification)}
                  onChange={(event) =>
                    setCampaign((prev) => ({ ...prev, send_in_app_notification: event.target.checked }))
                  }
                  disabled={isReadOnly}
                />
              )}
              label="In-app notification"
            />
          </Stack>
        </Stack>

        {isReadOnly ? (
          <Alert severity="info">
            This campaign is in <strong>{campaign.status}</strong> status and is now read-only.
          </Alert>
        ) : null}

        {!isReadOnly ? (
          <Alert severity="info">
            Recommended flow: set audience, design content (Visual Builder), send test, then schedule.
          </Alert>
        ) : null}

        {error ? <Alert severity="error">{error}</Alert> : null}
        {success ? <Alert severity="success">{success}</Alert> : null}

        <Paper variant="outlined" sx={{ p: 2.5 }}>
          <Stepper activeStep={step} alternativeLabel>
            {STEPS.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <Box sx={{ mt: 3 }}>{stepContent}</Box>

          <Stack direction="row" spacing={1.5} justifyContent="space-between" sx={{ mt: 3 }}>
            <Button onClick={() => navigate('/admin/communications/email-campaigns')}>
              Back to List
            </Button>

            <Stack direction="row" spacing={1.5}>
              <Button onClick={previousStep} disabled={step === 0}>
                Previous
              </Button>

              {step < STEPS.length - 1 ? (
                <Button variant="contained" onClick={nextStep} disabled={saving || confirmLoading}>
                  {saving ? 'Saving...' : 'Save & Next'}
                </Button>
              ) : null}
            </Stack>
          </Stack>
        </Paper>
      </Stack>

      <EmailCampaignPreviewDialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        html={previewHtml}
      />
      <EmailCampaignTourDialog
        open={tourOpen}
        onClose={() => setTourOpen(false)}
        onDismiss={finishTour}
        onFinish={finishTour}
        title="Campaign Builder Tour"
        steps={BUILDER_TOUR_STEPS}
      />
    </Box>
  );
}
